import { AppShell, Box, Button, Group, Menu, ScrollArea, Stack, Text, Tooltip } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useState, type ReactNode } from 'react'

import { useAuth } from './auth/AuthContext'
import { BottomNav, type NavItem } from './components/BottomNav'
import { Fab, FabVisibilityProvider } from './components/Fab'
import { useCompactNav } from './components/useIsMobile'
import { IconAdd, IconDownload, IconKeyboardArrowDown, IconLogout, IconSettings } from './components/icons'
import { NavLinkLink } from './components/links'
import { useExportData } from './components/useExportData'
import { useSetupState } from './features/setup/useSetupState'
import { needsFirstRun, readSetupLater } from './lib/firstRun'
import { BOTTOM_NAV_ITEMS, isActivePath, NAV_SECTIONS, SETTINGS_NAV_ITEM } from './lib/navigation'

/** The expanded rail's row: icon, label and the pill active indicator (today's sidebar, restyled, §11.3). */
function NavItemLink(props: { item: NavItem; pathname: string }) {
  const active = isActivePath(props.pathname, props.item.to)
  const Icon = props.item.icon
  return (
    <NavLinkLink
      to={props.item.to}
      label={props.item.label}
      leftSection={<Icon size={20} filled={active} />}
      active={active}
      aria-current={active ? 'page' : undefined}
      styles={{ label: { fontWeight: active ? 600 : 500 } }}
    />
  )
}

/** The collapsed rail's row: icon only, centred, with the label as a tooltip (M3 navigation rail, §11.3). */
function RailIcon(props: { item: NavItem; pathname: string }) {
  const active = isActivePath(props.pathname, props.item.to)
  const Icon = props.item.icon
  return (
    <Tooltip label={props.item.label} position="right">
      <Link
        to={props.item.to}
        className="nav-rail-item"
        data-active={active || undefined}
        aria-label={props.item.label}
        aria-current={active ? 'page' : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 56,
          height: 56,
          margin: '0 auto',
          borderRadius: 16,
          color: active ? 'var(--mt-on-selected)' : 'var(--mantine-color-dimmed)',
        }}
      >
        <Icon size={22} filled={active} />
      </Link>
    </Tooltip>
  )
}

/**
 * Sends a first-time user to guided setup once SETTINGS and GOAL have loaded with no profile and no goal (UX spec
 * §3.1), unless they chose "Finish setup later". It runs on every page, so a deep link still lands in setup.
 */
function useFirstRunRedirect() {
  const navigate = useNavigate()
  const { loaded, state } = useSetupState()
  const redirect = needsFirstRun(loaded, state, readSetupLater())
  useEffect(() => {
    // Replace, so Back from setup does not land on the page that redirected and bounce straight back.
    if (redirect) void navigate({ to: '/setup/$step', params: { step: '0' }, replace: true })
  }, [redirect, navigate])
}

function AccountMenu() {
  const auth = useAuth()
  const navigate = useNavigate()
  const exportData = useExportData()
  return (
    <Menu>
      <Menu.Target>
        <Button variant="subtle" color="gray" size="compact-sm" rightSection={<IconKeyboardArrowDown size={18} />}>
          <Text span size="sm" maw={200} truncate>
            {auth.email ?? 'Account'}
          </Text>
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<IconDownload size={18} />} onClick={() => void exportData()}>
          Export my data
        </Menu.Item>
        <Menu.Item leftSection={<IconSettings size={18} />} onClick={() => void navigate({ to: '/settings/$group', params: { group: 'data' } })}>
          Account and data
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item color="red" leftSection={<IconLogout size={18} />} onClick={() => void auth.signOut()}>
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}

/**
 * The signed-in shell (§11.3, §11.5 phase 2): a bottom nav bar + FAB below 600 px (mirrors the phone, owner
 * decision §12 #4); a collapsed icon rail 600–1199 px; the expanded rail (today's sidebar restyled) at 1200 px and
 * up. No drawer at any size ("never use the navigation rail and navigation bar simultaneously", and M3's drawer is
 * "no longer recommended"). [children] replaces the routed page (not found).
 */
export function Layout(props: { children?: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const navigate = useNavigate()
  useFirstRunRedirect()

  // M3 breakpoints (§9.1): compact <600, medium 600–839, expanded 840–1199, large/XL ≥1200.
  const mobile = useCompactNav()
  const wideRail = useMediaQuery('(min-width: 75em)') ?? false
  const railWidth = wideRail ? 240 : 96
  // The Food log's selection toolbar hides the FAB rather than sit under it (Fab.tsx's useHideFab, §12 #12's web
  // equivalent); its own content then needs less reserved space below than the FAB normally takes.
  const [fabHidden, setFabHidden] = useState(false)

  const openAddFood = () => void navigate({ to: '/log', search: { add: true } })

  return (
    <FabVisibilityProvider value={setFabHidden}>
      <AppShell
        header={{ height: 56 }}
        navbar={mobile ? undefined : { width: railWidth, breakpoint: 0 }}
        footer={mobile ? { height: 80 } : undefined}
        padding="md"
      >
        <a href="#main-content" className="skip-link">
          Skip to content
        </a>
        <AppShell.Header>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Text component={Link} to="/dashboard" fw={600} fz={20} lh={1} c="inherit" td="none" style={{ letterSpacing: '0.02em' }} aria-label="Macrotide, go to the dashboard">
              Macrotide
            </Text>
            <AccountMenu />
          </Group>
        </AppShell.Header>

        {!mobile && (
          <AppShell.Navbar className="nav-rail" aria-label="Main">
            <AppShell.Section grow component={ScrollArea} px={wideRail ? 'xs' : 4} py="sm">
              <Stack gap="md">
                {NAV_SECTIONS.map((section) => (
                  <Box key={section.label} component="section" aria-label={section.label}>
                    {wideRail && (
                      <Text size="xs" fw={600} c="dimmed" tt="none" px="sm" mb={4}>
                        {section.label}
                      </Text>
                    )}
                    <Stack gap={wideRail ? 0 : 'xs'}>
                      {section.items.map((item) => (wideRail ? <NavItemLink key={item.to} item={item} pathname={pathname} /> : <RailIcon key={item.to} item={item} pathname={pathname} />))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </AppShell.Section>
            <AppShell.Section px={wideRail ? 'xs' : 4} py="sm" style={{ borderTop: '1px solid var(--mt-hairline)' }}>
              {wideRail ? <NavItemLink item={SETTINGS_NAV_ITEM} pathname={pathname} /> : <RailIcon item={SETTINGS_NAV_ITEM} pathname={pathname} />}
            </AppShell.Section>
          </AppShell.Navbar>
        )}

        <AppShell.Main>
          {/* The FAB floats outside AppShell's own footer-height padding, so mobile pages need extra clearance below
              their last item while it's showing (owner's device test, 24 Oct 2026: a page's end was covered). */}
          <Box id="main-content" tabIndex={-1} maw={1100} mx="auto" pb={mobile && !fabHidden ? 96 : 'xl'} style={{ outline: 'none' }}>
            {props.children ?? <Outlet />}
          </Box>
        </AppShell.Main>

        {mobile && (
          <AppShell.Footer>
            <BottomNav items={BOTTOM_NAV_ITEMS} pathname={pathname} />
          </AppShell.Footer>
        )}
        {mobile && !fabHidden && <Fab label="Add food" icon={<IconAdd size={20} />} onClick={openAddFood} />}
      </AppShell>
    </FabVisibilityProvider>
  )
}
